import ReactDOM from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';
import TrackingProvider from '@elementary/common-ui/providers/tracking';
import { FronteggProvider } from '@frontegg/react';
import * as Sentry from '@sentry/react';
import { BrowserRouter } from 'react-router-dom';
import App from 'app';
import AppProviders from './AppProviders';
import './axiosInject';
import './suppressConsole';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [new Sentry.BrowserTracing(), new Sentry.Replay()],

    // Set tracesSampleRate to 1.0 to capture 100%
    // of transactions for performance monitoring.
    tracesSampleRate: 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_INFRA_ENV,
  });
}

if (import.meta.env.VITE_DATADOG_APPLICATION_ID) {
  datadogRum.init({
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'elementary-cloud',
    env: import.meta.env.VITE_INFRA_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      {
        match: new RegExp(
          import.meta.env.VITE_API_BASE_URL?.replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&',
          ),
        ),
        propagatorTypes: ['tracecontext', 'datadog'],
      },
    ],
  });
}

const contextOptions = {
  baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
  clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
  tenantResolver: () => {
    const urlQueryParams = new URLSearchParams(window.location.search);
    const organization = urlQueryParams.get('organization');
    return {
      tenant: organization,
    };
  },
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <BrowserRouter>
    <FronteggProvider
      contextOptions={contextOptions}
      authOptions={{
        keepSessionAlive: true,
      }}
      hostedLoginBox
    >
      <AppProviders>
        <TrackingProvider>
          <App />
        </TrackingProvider>
      </AppProviders>
    </FronteggProvider>
  </BrowserRouter>,
);
